import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, FileText, MessageSquare, Hash, Phone, AlertCircle, CheckCircle,
  RotateCcw, Copy, Info, ChevronRight, Eye, Zap, Shield, X, Users,
  Sparkles, ArrowLeft, ArrowRight, ClipboardPaste, Loader2, Type,
  CheckCircle2, XCircle, Clock, BarChart3, Smartphone, Wifi, Battery,
  Signal, BookUser, FolderOpen, Upload, Download
} from 'lucide-react';
import api from '../services/api';
import SearchableDropdown from '../components/common/SearchableDropdown';
import { useAuth } from '../context/AuthContext';

// DLT approved variable tags
const DLT_VARIABLE_TAGS = [
  { tag: '{#var#}', label: 'Type Here', description: 'Any text value', color: '#d97706' },
  { tag: '{#numeric#}', label: 'Numeric', description: 'OTPs, amounts', color: '#dc2626' },
  { tag: '{#alphanumeric#}', label: 'Alphanumeric', description: 'Reference IDs', color: '#7c3aed' },
  { tag: '{#url#}', label: 'URL', description: 'Website links', color: '#2563eb' },
  { tag: '{#urlott#}', label: 'URL OTT', description: 'App links', color: '#0891b2' },
  { tag: '{#cbn#}', label: 'Callback', description: 'Callback numbers', color: '#059669' },
  { tag: '{#email#}', label: 'Email', description: 'Email addresses', color: '#db2777' },
];

const DLT_VAR_REGEX = /\{#(?:var|numeric|alphanumeric|url|urlott|cbn|email)#\}/g;

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

// ─── Step indicator ───
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center gap-1">
    {steps.map((step, i) => {
      const isActive = i === currentStep;
      const isDone = i < currentStep;
      return (
        <React.Fragment key={step.id}>
          {i > 0 && (
            <div className="w-8 sm:w-12 h-[2px] rounded-full transition-all duration-300" style={{
              background: isDone ? 'linear-gradient(90deg, #4f46e5, #06b6d4)' : 'rgba(99, 102, 241, 0.12)',
            }} />
          )}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all duration-300" style={{
              background: isDone ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : isActive ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.04)',
              color: isDone ? '#fff' : isActive ? '#4f46e5' : '#94a3b8',
              border: isActive ? '2px solid rgba(99, 102, 241, 0.3)' : '2px solid transparent',
            }}>
              {isDone ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-semibold" style={{ color: isDone || isActive ? '#1e293b' : '#94a3b8' }}>{step.label}</p>
              <p className="text-[10px]" style={{ color: isDone || isActive ? '#64748b' : '#cbd5e1' }}>{step.sub}</p>
            </div>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Mobile Phone Preview ───
const MobilePreview = ({ message, senderName, encodingType, smsCount, charCount }) => {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const hasMessage = message && message.trim().length > 0;
  const hasUnfilledVars = DLT_VAR_REGEX.test(message || '');

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-1.5 mb-3">
        <Smartphone className="w-4 h-4" style={{ color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Phone Preview</h3>
      </div>

      {/* Phone Frame */}
      <div className="relative" style={{ width: '280px' }}>
        {/* Outer frame */}
        <div className="rounded-[2.2rem] p-[3px]" style={{
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 8px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
        }}>
          {/* Inner bezel */}
          <div className="rounded-[2rem] overflow-hidden" style={{
            background: '#000',
            border: '2px solid #2a2a3e',
          }}>
            {/* Dynamic Island / Notch */}
            <div className="flex justify-center pt-2 pb-1" style={{ background: '#000' }}>
              <div className="w-[90px] h-[26px] rounded-full flex items-center justify-center gap-2" style={{
                background: '#111',
                border: '1px solid #222',
              }}>
                <div className="w-[8px] h-[8px] rounded-full" style={{ background: '#1a1a2e', border: '1px solid #333' }} />
                <div className="w-[6px] h-[6px] rounded-full" style={{ background: '#0a84ff' }} />
              </div>
            </div>

            {/* Status Bar */}
            <div className="flex items-center justify-between px-5 py-1" style={{ background: '#000' }}>
              <span className="text-[11px] font-semibold text-white/90">{timeStr}</span>
              <div className="flex items-center gap-1.5">
                <Signal className="w-3 h-3 text-white/70" />
                <Wifi className="w-3 h-3 text-white/70" />
                <Battery className="w-3.5 h-3.5 text-white/70" />
              </div>
            </div>

            {/* Messages App Header */}
            <div className="px-4 py-2.5 flex items-center gap-2.5" style={{
              background: 'linear-gradient(180deg, #f8f9fa 0%, #f0f1f3 100%)',
              borderBottom: '1px solid #e0e0e0',
            }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
              }}>
                <span className="text-[11px] font-bold text-white">{(senderName || 'SM').substring(0, 2).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-gray-900 truncate">{senderName || 'Sender'}</p>
                <p className="text-[10px] text-gray-500">SMS Message</p>
              </div>
              <Phone className="w-4 h-4 text-gray-400" />
            </div>

            {/* Message Area */}
            <div className="px-3 py-4" style={{
              background: 'linear-gradient(180deg, #e8ecf0 0%, #dfe3e8 100%)',
              minHeight: '320px',
              maxHeight: '320px',
              overflowY: 'auto',
            }}>
              {hasMessage ? (
                <div className="flex justify-start">
                  <div className="max-w-[220px]">
                    {/* Timestamp */}
                    <div className="flex justify-center mb-3">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium" style={{
                        background: 'rgba(0,0,0,0.06)',
                        color: '#8e8e93',
                      }}>Today {timeStr}</span>
                    </div>
                    {/* Message bubble */}
                    <div className="relative">
                      <div className="px-3 py-2 rounded-2xl rounded-tl-md text-[13px] leading-[1.35] break-words" style={{
                        background: '#fff',
                        color: '#1c1c1e',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      }}>
                        {/* Render message with unfilled vars highlighted */}
                        {hasUnfilledVars ? (
                          message.split(DLT_VAR_REGEX).map((part, i) => {
                            const tags = message.match(DLT_VAR_REGEX) || [];
                            const elements = [<span key={`p-${i}`}>{part}</span>];
                            if (i < tags.length) {
                              elements.push(
                                <span key={`v-${i}`} className="inline-block px-1 py-0.5 mx-0.5 rounded text-[10px] font-bold" style={{
                                  background: 'rgba(245, 158, 11, 0.15)',
                                  color: '#d97706',
                                  border: '1px dashed rgba(245, 158, 11, 0.4)',
                                }}>___</span>
                              );
                            }
                            return elements;
                          })
                        ) : (
                          message
                        )}
                      </div>
                      {/* Delivery status */}
                      <div className="flex items-center justify-end gap-1 mt-1 pr-1">
                        <span className="text-[10px]" style={{ color: '#8e8e93' }}>{timeStr}</span>
                      </div>
                    </div>
                    {/* SMS info badge */}
                    <div className="flex items-center gap-2 mt-2 px-1">
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{
                        background: 'rgba(0,0,0,0.05)',
                        color: '#8e8e93',
                      }}>{charCount} chars</span>
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{
                        background: smsCount > 1 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0,0,0,0.05)',
                        color: smsCount > 1 ? '#d97706' : '#8e8e93',
                      }}>{smsCount} SMS</span>
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{
                        background: 'rgba(0,0,0,0.05)',
                        color: '#8e8e93',
                      }}>{encodingType}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full pt-16 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(0,0,0,0.04)' }}>
                    <MessageSquare className="w-5 h-5" style={{ color: '#c7c7cc' }} />
                  </div>
                  <p className="text-[12px] font-medium" style={{ color: '#8e8e93' }}>No message yet</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#aeaeb2' }}>Start typing to see preview</p>
                </div>
              )}
            </div>

            {/* Bottom bar (home indicator) */}
            <div className="flex justify-center py-2" style={{ background: '#e8ecf0' }}>
              <div className="w-[120px] h-[4px] rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SendSMSNew = ({ isAdmin = false }) => {
  const { user } = useAuth();
  // ─── State ───
  const [currentStep, setCurrentStep] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [originalTemplate, setOriginalTemplate] = useState('');
  const [editableMessage, setEditableMessage] = useState('');
  const [encodingType, setEncodingType] = useState('GSM');
  const [variables, setVariables] = useState([]);
  const [variableValues, setVariableValues] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState('');
  const [validNumbers, setValidNumbers] = useState([]);
  const [invalidNumbers, setInvalidNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingHeaders, setLoadingHeaders] = useState(false);
  const [headersError, setHeadersError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState('');
  const [mismatchDetails, setMismatchDetails] = useState(null);
  const [pasteToast, setPasteToast] = useState({ show: false, type: '', message: '' });
  const overlayRef = useRef(null);
  const templateDropdownRef = useRef(null);
  const pasteTextareaRef = useRef(null);
  const [showVariables, setShowVariables] = useState(true);

  // Contact Book Group Selection
  const [contactGroups, setContactGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupMobiles, setGroupMobiles] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingGroupMobiles, setLoadingGroupMobiles] = useState(false);
  const [groupLimitAlert, setGroupLimitAlert] = useState('');

  // File Upload for Mobile Numbers
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedNumbers, setUploadedNumbers] = useState([]);
  const [uploadedInvalid, setUploadedInvalid] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const steps = [
    { id: 'select', label: 'Select Template', sub: 'Header & Template' },
    { id: 'compose', label: 'Compose Message', sub: 'Variables & Preview' },
    { id: 'send', label: 'Send', sub: 'Recipients & Submit' },
  ];

  // ─── Load headers + contact groups on mount ───
  useEffect(() => { loadHeaders(); loadContactGroups(); }, []);

  // ─── Fetch group mobiles when groups are selected ───
  useEffect(() => {
    if (selectedGroups.length === 0) {
      setGroupMobiles([]);
      setGroupLimitAlert('');
      return;
    }
    const fetchGroupMobiles = async () => {
      setLoadingGroupMobiles(true);
      setGroupLimitAlert('');
      try {
        const allMobiles = [];
        for (const gid of selectedGroups) {
          const res = await api.get(`/user/contact-book/groups/${gid}/mobiles`);
          if (res.data?.mobiles) allMobiles.push(...res.data.mobiles);
        }
        // Deduplicate
        const unique = [...new Set(allMobiles)];
        if (unique.length > 500000) {
          setGroupLimitAlert(`Selected groups contain ${unique.length.toLocaleString()} numbers which exceeds the 500,000 limit. Only the first 500,000 will be used.`);
          setGroupMobiles(unique.slice(0, 500000));
        } else {
          setGroupMobiles(unique);
        }
      } catch (err) {
        console.error('Failed to fetch group mobiles:', err);
      } finally {
        setLoadingGroupMobiles(false);
      }
    };
    fetchGroupMobiles();
  }, [selectedGroups]);

  // ─── Auto-detect encoding ───
  useEffect(() => {
    if (!editableMessage) return;
    const gsmChars = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜa-zäöñüà§^{}\\\[~\]|€\s]*$/;
    setEncodingType(gsmChars.test(editableMessage) ? 'GSM' : 'UCS-2');
  }, [editableMessage]);

  // ─── API calls ───
  const loadHeaders = async () => {
    setLoadingHeaders(true);
    setHeadersError('');
    try {
      if (isAdmin) {
        // Admin: load ALL headers without restriction
        const response = await api.get('/admin/headers', { params: { page: 1, page_size: 5000 } });
        const headersList = response.data?.data || [];
        setHeaders(headersList.map(h => ({ id: h.id, header_name: h.header_name, entity_id: h.entity_id, entity_name: h.entity_name, header_type: h.header_type })));
        if (headersList.length === 0) setHeadersError('No headers found in the system.');
      } else {
        const response = await api.get('/user/headers');
        if (response.data.success && response.data.headers) {
          setHeaders(response.data.headers);
          if (response.data.headers.length === 0) setHeadersError('No headers mapped to your account.');
        } else {
          setHeaders([]);
          setHeadersError('No headers available');
        }
      }
    } catch (err) {
      setHeadersError(err.response?.data?.error || 'Unable to fetch headers.');
      setHeaders([]);
    } finally {
      setLoadingHeaders(false);
    }
  };

  const loadContactGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await api.get('/user/contact-book/groups-for-sms');
      setContactGroups(res.data?.groups || []);
    } catch (err) {
      console.error('Failed to load contact groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) return prev.filter(id => id !== groupId);
      return [...prev, groupId];
    });
  };

  const loadTemplates = async (headerId) => {
    if (!headerId) { setTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const endpoint = isAdmin ? `/admin/header-templates/${headerId}` : `/user/headers/${headerId}/templates`;
      const response = await api.get(endpoint);
      // Admin endpoint returns plain array, user endpoint returns { success, templates }
      const tplList = Array.isArray(response.data) ? response.data : (response.data?.templates || response.data?.data || []);
      setTemplates(Array.isArray(tplList) ? tplList : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ─── Handlers ───
  const handleHeaderChange = (value) => {
    setSelectedHeader(value);
    setSelectedTemplate('');
    setTemplateId('');
    setTemplateName('');
    setTemplateType('');
    setOriginalTemplate('');
    setEditableMessage('');
    setTemplates([]);
    setVariables([]);
    setVariableValues([]);
    setPasteText('');
    setPasteError('');
    if (value) {
      loadTemplates(value);
      // Auto-focus template dropdown after header selection
      setTimeout(() => {
        if (templateDropdownRef.current) {
          templateDropdownRef.current.focus();
          templateDropdownRef.current.click();
        }
      }, 300);
    }
  };

  const handleTemplateChange = async (value) => {
    setSelectedTemplate(value);
    if (!value) {
      setTemplateId(''); setTemplateName(''); setTemplateType('');
      setOriginalTemplate(''); setEditableMessage('');
      setVariables([]); setVariableValues([]);
      setPasteText(''); setPasteError('');
      return;
    }
    try {
      const response = await api.get(`/user/templates/${value}`);
      if (response.data.success && response.data.template) {
        const content = response.data.template.template_content;
        setTemplateId(response.data.template.template_id);
        setTemplateName(response.data.template.template_name || '');
        setTemplateType(response.data.template.template_type || '');
        setOriginalTemplate(content);
        setEditableMessage(content);
        setPasteText(content);
        const matches = content.match(DLT_VAR_REGEX) || [];
        const varObjects = matches.map((tag, idx) => {
          const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
          return { tag, index: idx, ...tagInfo };
        });
        setVariables(varObjects);
        setVariableValues(Array(matches.length).fill(''));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load template content');
    }
  };

  const generatePreviewMessage = useCallback((values) => {
    let preview = originalTemplate;
    let vi = 0;
    preview = preview.replace(DLT_VAR_REGEX, (match) => {
      const replacement = values[vi] || match;
      vi++;
      return replacement;
    });
    return preview;
  }, [originalTemplate]);

  const handleVariableChange = (index, value) => {
    if (value.length > 30) return;
    const newValues = [...variableValues];
    newValues[index] = value;
    setVariableValues(newValues);
    const preview = generatePreviewMessage(newValues);
    setEditableMessage(preview);
    setPasteText(preview);
  };

  const handleResetVariables = () => {
    setEditableMessage(originalTemplate);
    setVariableValues(Array(variables.length).fill(''));
    setPasteText(originalTemplate);
    setPasteError('');
    setMismatchDetails(null);
  };

  // ─── Paste extraction ───
  const extractVariablesFromText = (text) => {
    if (!text?.trim()) return { success: false, error: 'No text to process' };
    if (!originalTemplate) return { success: false, error: 'Select a template first' };
    const parts = originalTemplate.split(DLT_VAR_REGEX);
    const varCount = parts.length - 1;
    if (varCount === 0) {
      if (text.trim() === originalTemplate.trim()) {
        return { success: true, values: [], static: true };
      }
      // Find where the static text differs
      const expected = originalTemplate.trim();
      const actual = text.trim();
      const diffs = findStaticMismatches(expected, actual);
      return { success: false, error: 'Static content mismatch — your message does not match the template', mismatches: diffs, templateParts: [expected], userText: actual };
    }
    const regexParts = parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regexStr = '^' + regexParts.join('(.+?)') + '$';
    let regex;
    try { regex = new RegExp(regexStr, 's'); } catch { return { success: false, error: 'Template pattern error' }; }
    const match = text.trim().match(regex);
    if (!match) {
      // Detailed mismatch: find which static parts don't match
      const mismatches = findTemplateMismatches(parts, text.trim());
      return { success: false, error: 'Static content mismatch — the fixed text in your message does not match the template', mismatches, templateParts: parts, userText: text.trim() };
    }
    const extracted = [];
    const tooLong = [];
    for (let i = 1; i <= varCount; i++) {
      const val = match[i] || '';
      extracted.push(val);
      if (val.length > 30) tooLong.push({ index: i, length: val.length });
    }
    if (tooLong.length > 0) return { success: false, error: `Variable values exceed 30 char limit` };
    return { success: true, values: extracted };
  };

  // Find which static parts of the template don't match the user's text
  const findTemplateMismatches = (staticParts, userText) => {
    const mismatches = [];
    let searchPos = 0;
    for (let i = 0; i < staticParts.length; i++) {
      const part = staticParts[i];
      if (!part) continue; // empty part (variable at start/end)
      const foundAt = userText.indexOf(part, searchPos);
      if (foundAt === -1) {
        // This static part is missing or different in user text
        // Try to find the closest match to show what the user typed instead
        const userChunk = userText.substring(searchPos, searchPos + part.length + 20);
        mismatches.push({
          partIndex: i,
          expected: part.length > 80 ? part.substring(0, 80) + '...' : part,
          got: userChunk.length > 80 ? userChunk.substring(0, 80) + '...' : (userChunk || '(missing)'),
        });
      } else {
        searchPos = foundAt + part.length;
      }
    }
    return mismatches;
  };

  // Find character-level diff for fully static templates
  const findStaticMismatches = (expected, actual) => {
    const mismatches = [];
    const minLen = Math.min(expected.length, actual.length);
    let diffStart = -1;
    for (let i = 0; i < minLen; i++) {
      if (expected[i] !== actual[i] && diffStart === -1) {
        diffStart = i;
      }
    }
    if (diffStart === -1 && expected.length !== actual.length) {
      diffStart = minLen;
    }
    if (diffStart !== -1) {
      const ctx = 20;
      const start = Math.max(0, diffStart - ctx);
      const end = Math.min(Math.max(expected.length, actual.length), diffStart + ctx + 30);
      mismatches.push({
        partIndex: 0,
        expected: (start > 0 ? '...' : '') + expected.substring(start, end) + (end < expected.length ? '...' : ''),
        got: (start > 0 ? '...' : '') + actual.substring(start, Math.min(actual.length, end)) + (Math.min(actual.length, end) < actual.length ? '...' : '') || '(missing)',
        position: diffStart,
      });
    }
    return mismatches;
  };

  const handlePasteApply = () => {
    const result = extractVariablesFromText(pasteText);
    if (result.success) {
      if (result.values.length > 0) {
        setVariableValues(result.values);
        setEditableMessage(generatePreviewMessage(result.values));
      }
      setPasteError('');
    } else {
      setPasteError(result.error);
    }
  };

  const handlePasteTextChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    setPasteError('');
    setMismatchDetails(null);
    if (!originalTemplate || variables.length === 0) {
      // Static template — validate exact match on every keystroke (debounced feel)
      if (originalTemplate && text.trim().length > 0 && text.trim() !== originalTemplate.trim()) {
        const result = extractVariablesFromText(text);
        if (!result.success) {
          setPasteError(result.error);
          if (result.mismatches && result.mismatches.length > 0) {
            setMismatchDetails({ mismatches: result.mismatches, templateParts: result.templateParts, userText: result.userText });
          }
        }
      }
      setEditableMessage(text);
      return;
    }
    const result = extractVariablesFromText(text);
    if (result.success && result.values.length > 0) {
      setVariableValues(result.values);
      setEditableMessage(generatePreviewMessage(result.values));
    } else {
      setEditableMessage(text);
      // Show mismatch error if static parts don't match
      if (!result.success && text.trim().length > 0) {
        setPasteError(result.error);
        if (result.mismatches && result.mismatches.length > 0) {
          setMismatchDetails({ mismatches: result.mismatches, templateParts: result.templateParts, userText: result.userText });
        }
      }
    }
  };

  const handleDirectPaste = (e) => {
    if (!originalTemplate || variables.length === 0) return;
    const pasted = e.clipboardData?.getData('text');
    if (!pasted || e.target.tagName === 'INPUT') return;
    e.preventDefault();
    const result = extractVariablesFromText(pasted);
    if (result.success && result.values.length > 0) {
      setVariableValues(result.values);
      setEditableMessage(generatePreviewMessage(result.values));
      setPasteToast({ show: true, type: 'success', message: `${result.values.length} variable${result.values.length > 1 ? 's' : ''} extracted` });
    } else {
      setPasteToast({ show: true, type: 'error', message: result.error || 'Could not extract variables' });
    }
    setTimeout(() => setPasteToast({ show: false, type: '', message: '' }), 3000);
  };

  // ─── Mobile number processing ───
  const processMobileNumbers = (numbersText) => {
    const numbers = numbersText.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
    const numericRegex = /^[0-9]+$/;
    const indianMobileRegex = /^(\+91|91)?[6-9]\d{9}$/;
    const validSet = new Set();
    const invalid = [];
    numbers.forEach(num => {
      const cleaned = num.replace(/[\s-]/g, '');
      if (!numericRegex.test(cleaned.replace(/^\+91|^91/, ''))) {
        if (!invalid.includes(num)) invalid.push(num);
      } else if (indianMobileRegex.test(cleaned)) {
        let normalized = cleaned;
        if (normalized.startsWith('+91')) normalized = normalized.substring(3);
        else if (normalized.startsWith('91') && normalized.length > 10) normalized = normalized.substring(2);
        validSet.add(normalized);
      } else {
        if (!invalid.includes(num)) invalid.push(num);
      }
    });
    return { valid: Array.from(validSet), invalid };
  };

  // ─── File upload for mobile numbers ───
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExts = /\.(csv|txt|xls|xlsx)$/i;
    if (!allowedExts.test(file.name)) {
      setUploadError('Please upload a CSV, TXT, XLS, or XLSX file');
      return;
    }

    setUploadingFile(true);
    setUploadError('');
    setUploadedFile(file);

    try {
      const text = await file.text();
      // Extract numbers: split by comma, newline, semicolon, tab, or space
      const rawNumbers = text.split(/[\n\r,;\t]+/).map(n => n.trim()).filter(n => n.length > 0);
      // Filter out header-like rows
      const numberLines = rawNumbers.filter(n => /\d/.test(n) && !/^(mobile|phone|number|name|sr|sno)/i.test(n));
      const numbersText = numberLines.join('\n');
      const { valid, invalid } = processMobileNumbers(numbersText);
      setUploadedNumbers(valid);
      setUploadedInvalid(invalid);
      if (valid.length === 0) {
        setUploadError(`No valid mobile numbers found in file. ${invalid.length} invalid entries.`);
      }
    } catch (err) {
      console.error('File parse error:', err);
      setUploadError('Failed to read file. Please ensure it is a valid text/CSV file.');
    } finally {
      setUploadingFile(false);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setUploadedNumbers([]);
    setUploadedInvalid([]);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadSampleFile = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}`;
    const timeStr = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const uname = user?.username || 'user';
    const fileName = `Sample-${uname}-${dateStr}-${timeStr}.csv`;
    const csvContent = [
      'mobile_number',
      '9876543210',
      '9123456789',
      '8765432109',
      '7654321098',
      '6543210987',
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleMobileNumbersChange = (e) => {
    const text = e.target.value;
    setMobileNumbers(text);
    const { valid, invalid } = processMobileNumbers(text);
    setValidNumbers(valid);
    setInvalidNumbers(invalid);
    if (validationErrors.mobile) setValidationErrors(prev => ({ ...prev, mobile: '' }));
  };

  // ─── Character stats ───
  const calculateCharacterStats = () => {
    const length = editableMessage.length;
    const isUnicode = encodingType === 'UCS-2';
    let smsCount = 1;
    if (isUnicode) smsCount = length <= 70 ? 1 : Math.ceil(length / 67);
    else smsCount = length <= 160 ? 1 : Math.ceil(length / 153);
    return { length, smsCount };
  };

  const stats = calculateCharacterStats();

  // ─── Step validation ───
  const canProceedFromStep = (step) => {
    if (step === 0) return !!selectedHeader && !!selectedTemplate && !!originalTemplate;
    if (step === 1) return !!editableMessage && !DLT_VAR_REGEX.test(editableMessage) && !pasteError;
    return true;
  };

  const goNext = () => {
    if (currentStep < steps.length - 1 && canProceedFromStep(currentStep)) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Auto-focus message textarea when entering compose step
      if (nextStep === 1) {
        setTimeout(() => {
          if (pasteTextareaRef.current) {
            pasteTextareaRef.current.focus();
          }
        }, 200);
      }
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // ─── Combine all recipients (manual + group + uploaded file) ───
  const allRecipients = React.useMemo(() => {
    const combined = new Set([...validNumbers, ...groupMobiles, ...uploadedNumbers]);
    return Array.from(combined);
  }, [validNumbers, groupMobiles, uploadedNumbers]);

  // ─── Submit (with batch processing for large groups) ───
  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setSubmitResult(null);
    setResponseTime(null);

    // Validate
    const errors = {};
    if (!selectedHeader) errors.header = 'Select a header';
    if (!selectedTemplate) errors.template = 'Select a template';
    if (!editableMessage.trim()) errors.message = 'Message is empty';
    if (DLT_VAR_REGEX.test(editableMessage)) errors.variables = 'Fill all variables';
    if (allRecipients.length === 0) errors.mobile = 'Add at least one valid number or select a group';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    const startTime = performance.now();
    try {
      const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
      const basePayload = {
        message: editableMessage,
        entity_id: selectedHeaderObj?.entity_id || '',
        header_id: selectedHeaderObj?.header_name || '',
        template_id: templateId,
        encoding_type: encodingType,
        message_type: 'transactional',
        channel: 'GUI',
      };

      // Batch processing: split into chunks of 5000 numbers for smooth ingestion
      const BATCH_SIZE = 5000;
      let totalAccepted = 0;
      let totalMessageIds = [];
      let lastResponse = null;

      if (allRecipients.length <= BATCH_SIZE) {
        // Single batch — send all at once
        const response = await api.post('/user/send-sms', {
          ...basePayload,
          to: allRecipients.join(','),
        });
        lastResponse = response.data;
        if (response.data.status === 'accepted') {
          totalAccepted = response.data.count || response.data.message_ids?.length || allRecipients.length;
          totalMessageIds = response.data.message_ids || [];
        } else {
          setError(response.data?.message || 'Failed to submit SMS.');
          setLoading(false);
          return;
        }
      } else {
        // Multi-batch — split into chunks for smooth processing
        const batches = [];
        for (let i = 0; i < allRecipients.length; i += BATCH_SIZE) {
          batches.push(allRecipients.slice(i, i + BATCH_SIZE));
        }

        for (let i = 0; i < batches.length; i++) {
          try {
            const response = await api.post('/user/send-sms', {
              ...basePayload,
              to: batches[i].join(','),
            });
            if (response.data.status === 'accepted') {
              totalAccepted += response.data.count || response.data.message_ids?.length || batches[i].length;
              if (response.data.message_ids) totalMessageIds.push(...response.data.message_ids);
            }
            lastResponse = response.data;
          } catch (batchErr) {
            console.error(`Batch ${i + 1}/${batches.length} failed:`, batchErr);
          }
          // Small delay between batches to avoid overwhelming the server
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }

        if (totalAccepted === 0) {
          setError('All batches failed to submit.');
          setLoading(false);
          return;
        }
      }

      const elapsed = Math.round(performance.now() - startTime);
      setResponseTime(elapsed);
      setSubmitResult({ ...lastResponse, count: totalAccepted, message_ids: totalMessageIds });
      setSuccess(`${totalAccepted.toLocaleString()} SMS accepted in ${elapsed}ms${allRecipients.length > BATCH_SIZE ? ` (${Math.ceil(allRecipients.length / BATCH_SIZE)} batches)` : ''}`);
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait and try again.');
      } else {
        const msg = errData?.message || 'Unable to send SMS.';
        const refId = errData?.reference_id || '';
        setError(refId ? `${msg} (Ref: ${refId})` : msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Full reset ───
  const handleFullReset = () => {
    setCurrentStep(0);
    setSelectedTemplate(''); setTemplateId(''); setTemplateName(''); setTemplateType('');
    setOriginalTemplate(''); setEditableMessage(''); setVariables([]); setVariableValues([]);
    setMobileNumbers(''); setValidNumbers([]); setInvalidNumbers([]);
    setUploadedFile(null); setUploadedNumbers([]); setUploadedInvalid([]); setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSelectedGroups([]); setGroupMobiles([]); setGroupLimitAlert('');
    setValidationErrors({}); setError(''); setSuccess(''); setSubmitResult(null);
    setPasteText(''); setPasteError(''); setMismatchDetails(null);
  };

  // ─── Derived ───
  const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
  const allVarsFilled = variables.length === 0 || variableValues.every(v => v && v.trim());
  const noUnfilledVars = !!editableMessage && !DLT_VAR_REGEX.test(editableMessage);

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* ─── Page Header ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Send className="w-5 h-5 text-white" />
            </div>
            Send SMS
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>Advanced</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Compose and send DLT-approved messages in 3 easy steps</p>
        </div>
      </div>

      {/* ─── Step Indicator ─── */}
      <div className="bg-white rounded-xl p-4" style={cardStyle}>
        <StepIndicator steps={steps} currentStep={currentStep} />
      </div>

      {/* ─── Success Result ─── */}
      {success && submitResult && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.2)' }}>
          <div className="px-5 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white">SMS Submitted Successfully</span>
            </div>
            <div className="flex items-center gap-3">
              {responseTime !== null && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                  <Zap className="w-3 h-3" /> {responseTime}ms
                </span>
              )}
              <button onClick={() => { setSuccess(''); setSubmitResult(null); }} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4" style={{ background: 'rgba(16, 185, 129, 0.03)' }}>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Count</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{submitResult.count || 0}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Campaign ID</p>
              <p className="text-xs font-mono text-gray-700 mt-1.5 truncate">{submitResult.campaign_id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reference ID</p>
              <div className="flex items-center gap-1 mt-1.5">
                <p className="text-xs font-mono text-gray-700 truncate">{submitResult.reference_id || 'N/A'}</p>
                {submitResult.reference_id && (
                  <button onClick={() => navigator.clipboard.writeText(submitResult.reference_id)} className="text-gray-400 hover:text-indigo-500 flex-shrink-0"><Copy className="w-3 h-3" /></button>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Response</p>
              <p className="text-xs font-mono mt-1.5" style={{ color: responseTime < 50 ? '#059669' : responseTime < 200 ? '#d97706' : '#dc2626' }}>
                {responseTime !== null ? `${responseTime}ms` : 'N/A'}
              </p>
            </div>
          </div>
          {submitResult.message_ids?.length > 0 && (
            <div className="px-5 py-3 flex items-center gap-2 flex-wrap" style={{ background: 'rgba(16, 185, 129, 0.02)', borderTop: '1px solid rgba(16, 185, 129, 0.1)' }}>
              <span className="text-[10px] font-bold text-gray-400 uppercase">Message IDs:</span>
              {submitResult.message_ids.map((id, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[11px] font-mono" style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#4f46e5' }}>{id}</span>
              ))}
            </div>
          )}
          <div className="px-5 py-3 flex justify-end" style={{ borderTop: '1px solid rgba(16, 185, 129, 0.08)' }}>
            <button onClick={handleFullReset} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Send className="w-3.5 h-3.5" /> Send Another
            </button>
          </div>
        </div>
      )}

      {/* ─── Error ─── */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{
          background: error.includes('Rate limit') ? 'rgba(245, 158, 11, 0.06)' : 'rgba(239, 68, 68, 0.06)',
          border: error.includes('Rate limit') ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(239, 68, 68, 0.15)',
        }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{
            background: error.includes('Rate limit') ? 'linear-gradient(135deg, #d97706, #f59e0b)' : 'linear-gradient(135deg, #dc2626, #ef4444)',
          }}>
            {error.includes('Rate limit') ? <Shield className="w-4 h-4 text-white" /> : <AlertCircle className="w-4 h-4 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: error.includes('Rate limit') ? '#92400e' : '#991b1b' }}>
              {error.includes('Rate limit') ? 'Rate Limit Exceeded' : 'Submission Failed'}
            </p>
            <p className="text-sm mt-0.5" style={{ color: error.includes('Rate limit') ? '#a16207' : '#b91c1c' }}>{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* STEP 1: SELECT TEMPLATE                            */}
      {/* ═══════════════════════════════════════════════════ */}
      {currentStep === 0 && !success && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6" style={cardStyle}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Choose Header & Template</h2>
            </div>

            <div className={`grid grid-cols-1 ${originalTemplate ? 'lg:grid-cols-5' : 'lg:grid-cols-2'} gap-6`}>
              {/* Left: Header + Template Selection (2 cols) */}
              <div className={originalTemplate ? 'lg:col-span-2' : 'lg:col-span-1'}>
                <div className="space-y-5">
                {/* Header */}
                <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                  <Type className="w-3 h-3" style={{ color: '#6366f1' }} /> Header (Sender ID) <span className="text-red-400">*</span>
                </label>
                {loadingHeaders ? (
                  <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#6366f1' }} />
                    <span className="text-sm text-gray-500">Loading headers...</span>
                  </div>
                ) : headersError ? (
                  <div className="p-3 rounded-lg flex items-start gap-2" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-amber-700">{headersError}</span>
                  </div>
                ) : (
                  <SearchableDropdown
                    options={headers.map(h => ({ value: h.id.toString(), label: `${h.header_name} (${h.header_type})` }))}
                    value={selectedHeader}
                    onChange={handleHeaderChange}
                    placeholder="Search & select header..."
                    clearable={false}
                  />
                )}
                {selectedHeaderObj && (
                  <div className="mt-2 px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#059669' }} />
                    <span className="text-xs text-gray-600">Entity: <strong className="text-gray-800">{selectedHeaderObj.entity_name || selectedHeaderObj.entity_id}</strong></span>
                  </div>
                )}
                </div>

              {/* Template */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                  <MessageSquare className="w-3 h-3" style={{ color: '#6366f1' }} /> Template <span className="text-red-400">*</span>
                </label>
                {!selectedHeader ? (
                  <div className="py-3 px-4 rounded-lg text-sm text-gray-400" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px dashed rgba(99, 102, 241, 0.15)' }}>
                    Select a header first
                  </div>
                ) : loadingTemplates ? (
                  <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#6366f1' }} />
                    <span className="text-sm text-gray-500">Loading templates...</span>
                  </div>
                ) : (
                  <>
                    <SearchableDropdown
                      ref={templateDropdownRef}
                      options={templates.map(t => ({ value: t.id.toString(), label: `${t.template_name} (${t.template_id})` }))}
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                      placeholder="Search & select template..."
                      clearable={false}
                    />
                    {templates.length === 0 && selectedHeader && !loadingTemplates && (
                      <p className="text-gray-400 text-xs mt-1.5">No templates found for this header</p>
                    )}
                  </>
                )}
              </div>
                </div>
              </div>

              {/* Right: Template Preview (3 cols) */}
              {originalTemplate && (
                <div className="lg:col-span-3">
                  <div className="h-full rounded-xl p-4" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4" style={{ color: '#6366f1' }} />
                      <h3 className="text-sm font-semibold text-gray-700">Template Preview</h3>
                      <span className="ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-md" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(6, 182, 212, 0.08))', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.2)', letterSpacing: '0.05em' }}>Read Only</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                      {[
                        { label: 'Name', value: templateName },
                        { label: 'Template ID', value: templateId, mono: true, noTruncate: true },
                        { label: 'Type', value: templateType },
                        { label: 'Variables', value: variables.length > 0 ? `${variables.length} var${variables.length > 1 ? 's' : ''}` : 'None', color: variables.length > 0 ? '#d97706' : '#059669' },
                        { label: 'SMS Parts', value: (() => { const len = originalTemplate.length; const isUni = /[^\x00-\x7F]/.test(originalTemplate); const cnt = isUni ? (len <= 70 ? 1 : Math.ceil(len / 67)) : (len <= 160 ? 1 : Math.ceil(len / 153)); return `${cnt} part${cnt > 1 ? 's' : ''} (${len} chars)`; })(), color: (() => { const len = originalTemplate.length; const isUni = /[^\x00-\x7F]/.test(originalTemplate); const cnt = isUni ? (len <= 70 ? 1 : Math.ceil(len / 67)) : (len <= 160 ? 1 : Math.ceil(len / 153)); return cnt > 1 ? '#d97706' : '#059669'; })() },
                      ].map(item => (
                        <div key={item.label} className="px-2.5 py-1.5 rounded-lg bg-white" style={{ border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                          <p className="text-[9px] font-bold text-gray-400 uppercase">{item.label}</p>
                          <p className={`text-xs font-semibold mt-0.5 ${item.noTruncate ? 'break-all' : 'truncate'} ${item.mono ? 'font-mono' : ''}`} style={{ color: item.color || '#1e293b' }} title={item.value || '-'}>{item.value || '-'}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template Content</p>
                    <div className="px-3 py-2.5 rounded-lg text-sm leading-relaxed text-gray-700 whitespace-pre-wrap break-words bg-white" style={{ border: '1px solid rgba(99, 102, 241, 0.08)', maxHeight: '180px', overflowY: 'auto' }}>
                      {originalTemplate.split(DLT_VAR_REGEX).map((part, index) => {
                        const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                        const elements = [<span key={`t-${index}`}>{part}</span>];
                        if (index < tagMatches.length) {
                          const tag = tagMatches[index];
                          const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
                          elements.push(
                            <span key={`v-${index}`} className="inline-flex items-center mx-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-bold" style={{ background: `${tagInfo.color}12`, color: tagInfo.color, border: `1px solid ${tagInfo.color}25` }}>
                              {tag}
                            </span>
                          );
                        }
                        return elements;
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Next button */}
          <div className="flex justify-end">
            <button onClick={goNext} disabled={!canProceedFromStep(0)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: canProceedFromStep(0) ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : '#94a3b8' }}>
              Continue to Compose <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* STEP 2: COMPOSE MESSAGE                            */}
      {/* ═══════════════════════════════════════════════════ */}
      {currentStep === 1 && !success && (
        <div className="space-y-4">
          <div className="flex gap-5 items-start">
            {/* ─── Left: Compose Area ─── */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Compose card */}
              <div className="bg-white rounded-xl p-6" style={cardStyle}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
                      <MessageSquare className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Compose Your Message</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => navigator.clipboard.writeText(editableMessage)} className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-all flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button type="button" onClick={handleResetVariables} className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-all flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ alignItems: 'stretch' }}>
                  {/* Left: Type / Paste message */}
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <ClipboardPaste className="w-4 h-4" style={{ color: '#6366f1' }} />
                      <h3 className="text-sm font-semibold text-gray-700">Type or Paste Message</h3>
                    </div>
                    <p className="text-xs text-gray-400 mb-2" style={{ minHeight: '32px' }}>Type or paste a message — variables are auto-extracted and synced everywhere in real-time.</p>
                    <textarea
                      ref={pasteTextareaRef}
                      value={pasteText}
                      onChange={handlePasteTextChange}
                      rows={6}
                      className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed flex-1"
                      style={{ border: pasteError ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid rgba(99, 102, 241, 0.12)', minHeight: '200px', background: pasteError ? 'rgba(239, 68, 68, 0.02)' : 'white' }}
                      placeholder="Type or paste your complete message here..."
                    />
                    {pasteError && (
                      <div className="mt-2 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                        <div className="flex items-start gap-1.5 px-3 py-2" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                          <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-red-700">{pasteError}</p>
                            {mismatchDetails && mismatchDetails.mismatches.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {mismatchDetails.mismatches.map((m, idx) => (
                                  <div key={idx} className="rounded-md overflow-hidden text-[11px]" style={{ border: '1px solid rgba(239, 68, 68, 0.12)' }}>
                                    <div className="flex items-start gap-1.5 px-2 py-1.5" style={{ background: 'rgba(16, 185, 129, 0.06)' }}>
                                      <span className="font-bold text-emerald-700 flex-shrink-0 mt-px">Expected:</span>
                                      <span className="text-emerald-800 font-mono break-all whitespace-pre-wrap">{m.expected}</span>
                                    </div>
                                    <div className="flex items-start gap-1.5 px-2 py-1.5" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                                      <span className="font-bold text-red-700 flex-shrink-0 mt-px">Your text:</span>
                                      <span className="text-red-800 font-mono break-all whitespace-pre-wrap">{m.got}</span>
                                    </div>
                                  </div>
                                ))}
                                <div className="px-2 py-1.5 rounded-md flex items-start gap-1.5" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                                  <Info className="w-3 h-3 text-indigo-400 mt-0.5 flex-shrink-0" />
                                  <p className="text-[10px] text-indigo-600">The static (fixed) parts of your message must match the DLT-approved template exactly. Only the variable portions (shown as tags like {'{#var#}'}) can be changed.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Show full template for reference */}
                        {mismatchDetails && (
                          <div className="px-3 py-2" style={{ background: 'rgba(99, 102, 241, 0.03)', borderTop: '1px solid rgba(239, 68, 68, 0.1)' }}>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Approved Template</p>
                            <p className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap break-all leading-relaxed">
                              {originalTemplate.split(DLT_VAR_REGEX).map((part, index) => {
                                const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                                const isMismatched = mismatchDetails.mismatches.some(m => m.partIndex === index);
                                const elements = [
                                  <span key={`tp-${index}`} style={{
                                    background: isMismatched ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.1)',
                                    color: isMismatched ? '#dc2626' : '#065f46',
                                    borderRadius: '2px',
                                    padding: part ? '0 2px' : '0',
                                    textDecoration: isMismatched ? 'wavy underline' : 'none',
                                    textDecorationColor: isMismatched ? '#ef4444' : undefined,
                                  }}>{part}</span>
                                ];
                                if (index < tagMatches.length) {
                                  elements.push(
                                    <span key={`tv-${index}`} className="inline-block mx-0.5 px-1 py-0.5 rounded text-[10px] font-bold" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706', border: '1px dashed rgba(217, 119, 6, 0.3)' }}>{tagMatches[index]}</span>
                                  );
                                }
                                return elements;
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: Live Preview */}
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-4 h-4" style={{ color: '#059669' }} />
                      <h3 className="text-sm font-semibold text-gray-700">Live Message Preview</h3>
                    </div>
                    <p className="text-xs text-gray-400 mb-2" style={{ minHeight: '32px' }}>Click on variable slots below to edit them directly. Paste (Ctrl+V) here to auto-fill.</p>
                    <div className="relative flex-1">
                      <div
                        ref={overlayRef}
                        onPaste={handleDirectPaste}
                        className="px-3 py-2.5 rounded-lg overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 select-text"
                        style={{ minHeight: '200px', maxHeight: '260px', border: noUnfilledVars ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(99, 102, 241, 0.12)', background: noUnfilledVars ? 'rgba(16, 185, 129, 0.02)' : 'white' }}>
                        {(() => {
                          const result = [];
                          const textParts = originalTemplate.split(DLT_VAR_REGEX);
                          const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                          let vi = 0;
                          textParts.forEach((part, index) => {
                            if (part) result.push(<span key={`text-${index}`}>{part}</span>);
                            if (index < tagMatches.length) {
                              const currentVi = vi;
                              const val = variableValues[currentVi] || '';
                              const varInfo = variables[currentVi] || {};
                              const tagColor = varInfo.color || '#d97706';
                              result.push(
                                <span key={`var-wrap-${index}`} className="inline-flex items-center mx-0.5" style={{ verticalAlign: 'baseline' }}>
                                  <span className="flex items-center rounded-md overflow-hidden" style={{
                                    border: val ? '1.5px solid rgba(16, 185, 129, 0.5)' : `1.5px dashed ${tagColor}60`,
                                    background: val ? 'rgba(16, 185, 129, 0.06)' : `${tagColor}08`,
                                  }}>
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold select-none" style={{
                                      background: val ? 'rgba(16, 185, 129, 0.15)' : `${tagColor}18`,
                                      color: val ? '#059669' : tagColor,
                                      borderRight: val ? '1px solid rgba(16, 185, 129, 0.2)' : `1px solid ${tagColor}25`,
                                    }}>{currentVi + 1}</span>
                                    <input
                                      type="text"
                                      value={val}
                                      onChange={(e) => handleVariableChange(currentVi, e.target.value)}
                                      maxLength={30}
                                      placeholder={varInfo.label || `Var ${currentVi + 1}`}
                                      className="border-0 outline-none bg-transparent text-sm font-semibold px-1.5 py-0.5"
                                      style={{
                                        width: Math.max(80, Math.min(200, (val.length || 8) * 8 + 24)) + 'px',
                                        color: val ? '#059669' : '#9ca3af',
                                        caretColor: '#4f46e5',
                                      }}
                                    />
                                  </span>
                                </span>
                              );
                              vi++;
                            }
                          });
                          return result;
                        })()}
                      </div>
                      {pasteToast.show && (
                        <div className="absolute top-2 right-2 px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg z-10"
                          style={{ background: pasteToast.type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)', color: 'white' }}>
                          {pasteToast.type === 'success' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                          {pasteToast.message}
                        </div>
                      )}
                    </div>
                    {/* Stats bar */}
                    <div className="mt-2 flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                      <span className="text-xs text-gray-500">Chars: <strong className="text-gray-800">{stats.length}</strong></span>
                      <span className="w-px h-3 bg-gray-200" />
                      <span className="text-xs text-gray-500">Parts: <strong style={{ color: stats.smsCount > 1 ? '#d97706' : '#059669' }}>{stats.smsCount}</strong></span>
                      <span className="w-px h-3 bg-gray-200" />
                      <span className="text-xs text-gray-500">Encoding: <strong className="text-gray-800">{encodingType}</strong></span>
                      <span className="ml-auto">
                        {noUnfilledVars ? (
                          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#059669' }}><CheckCircle2 className="w-3 h-3" /> Ready</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#d97706' }}><Clock className="w-3 h-3" /> Fill variables</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Variable Cards */}
                {variables.length > 0 && (
                  <div className="mt-5 pt-5" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Hash className="w-4 h-4" style={{ color: '#d97706' }} />
                      <h3 className="text-sm font-semibold text-gray-700">Dynamic Variables</h3>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706' }}>{variables.length}</span>
                      <span className="text-[10px] text-gray-400 ml-1">Max 30 chars each</span>
                      <button
                        type="button"
                        onClick={() => setShowVariables(!showVariables)}
                        className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
                        style={{ border: '1px solid rgba(99, 102, 241, 0.12)', color: showVariables ? '#4f46e5' : '#94a3b8', background: showVariables ? 'rgba(99, 102, 241, 0.04)' : 'transparent' }}
                      >
                        <Eye className="w-3 h-3" /> {showVariables ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {showVariables && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {variables.map((varObj, index) => {
                        const tagColor = varObj.color || '#d97706';
                        const filled = variableValues[index] && variableValues[index].trim();
                        return (
                          <div key={`var_${index}`} className="relative rounded-xl p-3 transition-all" style={{
                            background: filled ? 'rgba(16, 185, 129, 0.03)' : `${tagColor}04`,
                            border: filled ? '1px solid rgba(16, 185, 129, 0.15)' : `1px solid ${tagColor}15`,
                          }}>
                            <div className="flex items-center gap-1.5 mb-2">
                              <span className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold" style={{ background: filled ? 'rgba(16, 185, 129, 0.12)' : `${tagColor}15`, color: filled ? '#059669' : tagColor }}>{index + 1}</span>
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: `${tagColor}10`, color: tagColor, border: `1px solid ${tagColor}20` }}>{varObj.tag}</span>
                              {filled && <CheckCircle2 className="w-3 h-3 ml-auto" style={{ color: '#059669' }} />}
                            </div>
                            <p className="text-[10px] text-gray-400 mb-1.5">{varObj.description}</p>
                            <input
                              type="text"
                              value={variableValues[index] || ''}
                              onChange={(e) => handleVariableChange(index, e.target.value)}
                              maxLength={30}
                              className="w-full px-2.5 py-2 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 transition-all bg-white"
                              style={{ border: filled ? '1px solid rgba(16, 185, 129, 0.2)' : `1px solid ${tagColor}20` }}
                              placeholder={varObj.description || `Value ${index + 1}`}
                            />
                            <span className="absolute right-4 bottom-[14px] text-[10px] text-gray-300">{variableValues[index]?.length || 0}/30</span>
                          </div>
                        );
                      })}
                    </div>}
                  </div>
                )}
              </div>
            </div>

            {/* ─── Right: Sticky Mobile Preview ─── */}
            <div className="hidden xl:block flex-shrink-0 sticky top-4" style={{ width: '310px' }}>
              <MobilePreview
                message={editableMessage}
                senderName={selectedHeaderObj?.header_name}
                encodingType={encodingType}
                smsCount={stats.smsCount}
                charCount={stats.length}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button onClick={goBack} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-white transition-all" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={goNext} disabled={!canProceedFromStep(1)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: canProceedFromStep(1) ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : '#94a3b8' }}>
              Continue to Send <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* STEP 3: RECIPIENTS & SEND                          */}
      {/* ═══════════════════════════════════════════════════ */}
      {currentStep === 2 && !success && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left: Recipients (2/3) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Contact Book Groups */}
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}>
                    <BookUser className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Select Contact Groups</h2>
                  {selectedGroups.length > 0 && (
                    <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(124, 58, 237, 0.08)', color: '#7c3aed' }}>
                      <FolderOpen className="w-3 h-3" /> {selectedGroups.length} group{selectedGroups.length > 1 ? 's' : ''} · {groupMobiles.length.toLocaleString()} numbers
                    </span>
                  )}
                </div>

                {loadingGroups ? (
                  <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading groups...</div>
                ) : contactGroups.length === 0 ? (
                  <div className="py-3 text-center">
                    <p className="text-xs text-gray-400">No contact groups with contacts. <a href="/contact-book" className="text-indigo-600 font-medium hover:underline">Create groups</a> in Contact Book first.</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {contactGroups.map((group) => {
                      const isSelected = selectedGroups.includes(group.id);
                      return (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => toggleGroupSelection(group.id)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                          style={{
                            background: isSelected ? 'rgba(124, 58, 237, 0.08)' : 'rgba(99, 102, 241, 0.03)',
                            border: isSelected ? '1.5px solid rgba(124, 58, 237, 0.4)' : '1px solid rgba(99, 102, 241, 0.1)',
                            color: isSelected ? '#7c3aed' : '#6b7280',
                          }}
                        >
                          {isSelected ? <CheckCircle className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                          {group.name}
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                            background: isSelected ? 'rgba(124, 58, 237, 0.12)' : 'rgba(99, 102, 241, 0.06)',
                            color: isSelected ? '#7c3aed' : '#94a3b8',
                          }}>{group.contact_count.toLocaleString()}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {loadingGroupMobiles && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-indigo-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading group numbers...</div>
                )}

                {groupLimitAlert && (
                  <div className="mt-3 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700 font-medium">{groupLimitAlert}</p>
                  </div>
                )}
              </div>

              {/* Upload Mobile Numbers from File */}
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
                    <Upload className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Upload Mobile Numbers</h2>
                  {uploadedNumbers.length > 0 && (
                    <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669' }}>
                      <CheckCircle className="w-3 h-3" /> {uploadedNumbers.length.toLocaleString()} valid
                    </span>
                  )}
                </div>

                {!uploadedFile ? (
                  <div>
                    <p className="text-xs text-gray-400 mb-3">Upload a CSV, TXT, XLS, or XLSX file with mobile numbers. One number per line or comma/semicolon separated.</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.txt,.xls,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                        style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1.5px dashed rgba(16, 185, 129, 0.3)', color: '#059669' }}
                      >
                        {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {uploadingFile ? 'Processing...' : 'Choose File to Upload'}
                      </button>
                      <button
                        type="button"
                        onClick={downloadSampleFile}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                        style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1.5px solid rgba(99, 102, 241, 0.15)', color: '#6366f1' }}
                      >
                        <Download className="w-3.5 h-3.5" /> Download Sample
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between p-3 rounded-lg mb-2" style={{ background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-xs font-semibold text-gray-800">{uploadedFile.name}</p>
                          <p className="text-[10px] text-gray-400">{(uploadedFile.size / 1024).toFixed(1)} KB · {uploadedNumbers.length.toLocaleString()} valid numbers{uploadedInvalid.length > 0 ? ` · ${uploadedInvalid.length} invalid` : ''}</p>
                        </div>
                      </div>
                      <button type="button" onClick={clearUploadedFile} className="p-1 rounded hover:bg-red-50 transition-colors">
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                    {uploadedInvalid.length > 0 && (
                      <div className="p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                        <p className="text-[10px] text-red-500">{uploadedInvalid.length} invalid entries skipped</p>
                      </div>
                    )}
                  </div>
                )}

                {uploadError && (
                  <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-600">{uploadError}</p>
                  </div>
                )}
              </div>

              {/* Manual Numbers */}
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0891b2, #06b6d4)' }}>
                    <Phone className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Manual Numbers</h2>
                  <div className="ml-auto flex items-center gap-2">
                    {validNumbers.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669' }}>
                        <CheckCircle className="w-3 h-3" /> {validNumbers.length} valid
                      </span>
                    )}
                    {invalidNumbers.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626' }}>
                        <XCircle className="w-3 h-3" /> {invalidNumbers.length} invalid
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-2">Enter Indian mobile numbers (10 digits). One per line or comma-separated. These will be combined with group numbers above.</p>

                <textarea
                  value={mobileNumbers}
                  onChange={handleMobileNumbersChange}
                  rows={6}
                  className="w-full px-3 py-2.5 font-mono text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 transition-all resize-none"
                  style={{
                    border: validationErrors.mobile ? '1px solid #fca5a5' : validNumbers.length > 0 ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(99, 102, 241, 0.15)',
                    background: validNumbers.length > 0 ? 'rgba(16, 185, 129, 0.02)' : 'white',
                  }}
                  placeholder={"9876543210\n9123456789\n+919876543210"}
                />
                {validationErrors.mobile && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.mobile}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="px-2 py-1 rounded text-[11px] text-gray-500" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>Formats: 9876543210 · +919876543210 · 919876543210</span>
                </div>

                {/* Invalid numbers detail */}
                {invalidNumbers.length > 0 && (
                  <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    <p className="text-xs font-semibold text-red-600 mb-1.5">Invalid numbers (will be skipped):</p>
                    <div className="flex flex-wrap gap-1">
                      {invalidNumbers.slice(0, 20).map((num, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-[11px] font-mono bg-red-50 text-red-600 border border-red-100">{num}</span>
                      ))}
                      {invalidNumbers.length > 20 && <span className="text-xs text-red-400">+{invalidNumbers.length - 20} more</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Combined Recipients Summary */}
              {([groupMobiles.length, uploadedNumbers.length, validNumbers.length].filter(n => n > 0).length >= 2) && (
                <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04), rgba(6, 182, 212, 0.03))', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  <Users className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-gray-800">Combined Recipients</p>
                    <p className="text-[10px] text-gray-500">
                      {[
                        groupMobiles.length > 0 && `${groupMobiles.length.toLocaleString()} from groups`,
                        uploadedNumbers.length > 0 && `${uploadedNumbers.length.toLocaleString()} from file`,
                        validNumbers.length > 0 && `${validNumbers.length.toLocaleString()} manual`,
                      ].filter(Boolean).join(' + ')} = <strong className="text-indigo-600">{allRecipients.length.toLocaleString()}</strong> unique numbers
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Summary (1/3) */}
            <div className="space-y-4">
              {/* SMS Summary */}
              <div className="bg-white rounded-xl p-5" style={{ ...cardStyle, background: 'rgba(99, 102, 241, 0.02)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-4 h-4" style={{ color: '#4f46e5' }} />
                  <h3 className="text-sm font-bold text-gray-800">SMS Summary</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Header', value: selectedHeaderObj?.header_name || '-' },
                    { label: 'Template', value: templateName || '-' },
                    { label: 'Characters', value: stats.length },
                    { label: 'SMS Parts', value: stats.smsCount, color: stats.smsCount > 1 ? '#d97706' : '#059669' },
                    { label: 'Encoding', value: encodingType },
                    { label: 'Recipients', value: allRecipients.length.toLocaleString(), color: allRecipients.length > 0 ? '#059669' : '#94a3b8' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{item.label}</span>
                      <span className="text-xs font-bold truncate ml-2" style={{ color: item.color || '#1e293b', maxWidth: '140px' }}>{item.value}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-gray-800">Total SMS</span>
                      <span className="text-lg font-bold" style={{ color: '#4f46e5' }}>{(stats.smsCount * allRecipients.length).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Message Preview */}
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4" style={{ color: '#059669' }} />
                  <h3 className="text-sm font-bold text-gray-800">Final Message</h3>
                </div>
                <div className="px-3 py-2.5 rounded-lg text-sm leading-relaxed text-gray-700 whitespace-pre-wrap break-words" style={{ background: 'rgba(16, 185, 129, 0.03)', border: '1px solid rgba(16, 185, 129, 0.12)', maxHeight: '200px', overflow: 'auto' }}>
                  {editableMessage || <span className="text-gray-400 italic">No message composed</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation + Send */}
          <div className="flex items-center justify-between">
            <button onClick={goBack} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-white transition-all" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleFullReset}
                className="px-3 py-2 rounded-xl text-xs font-medium text-gray-500 hover:bg-white transition-all" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                Reset All
              </button>
              <button onClick={handleSubmit} disabled={loading || allRecipients.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ background: loading ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4" /> Send {allRecipients.length > 0 ? `${(stats.smsCount * allRecipients.length).toLocaleString()} SMS` : 'SMS'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSMSNew;
